import { Component, useEffect, type ReactNode } from 'react';
import { AppProvider, Page, Layout, Card, Text, BlockStack } from '@shopify/polaris';
import { MemoryRouter, Routes, Route } from 'react-router-dom';
import enTranslations from '@shopify/polaris/locales/en.json';
import './styles/brand-theme.css';

import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Products from './pages/Products';
import Analytics from './pages/Analytics';
import Billing from './pages/Billing';

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <AppProvider i18n={enTranslations}>
          <Page title="Modera Fashion">
            <Layout>
              <Layout.Section>
                <Card>
                  <BlockStack gap="400">
                    <Text as="h2" variant="headingMd" tone="critical">
                      Something went wrong
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {this.state.error.message}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Please reload the page. If the problem persists, contact support.
                    </Text>
                  </BlockStack>
                </Card>
              </Layout.Section>
            </Layout>
          </Page>
        </AppProvider>
      );
    }
    return this.props.children;
  }
}

function App() {
  useEffect(() => {
    // Explicitly signal App Bridge that the app finished loading.
    // In v4 this should be automatic, but calling loading(false) prevents
    // the loading overlay from getting stuck in edge cases on some stores.
    if (typeof window !== 'undefined' && window.shopify?.loading) {
      window.shopify.loading(false);
    }
  }, []);

  return (
    <ErrorBoundary>
      <AppProvider i18n={enTranslations}>
        <MemoryRouter>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/products" element={<Products />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/billing" element={<Billing />} />
          </Routes>
        </MemoryRouter>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;
